import React, { lazy } from 'react'
import { Switch, Redirect, Route } from 'react-router-dom'

import * as R from 'ramda'
import { connect } from 'react-redux'
import { lifecycle, compose, withState } from 'recompose'
import { VisibilityProvider } from 'hooks/useVisibility'
import { QueryClientProvider, QueryClient } from 'react-query'

import { withStyles } from '@material-ui/core/styles'
import MuiLinearProgress from '@material-ui/core/LinearProgress'
import MuiCircularProgress from '@material-ui/core/CircularProgress'

import CsgCentralizedTemplate from 'components/templates/Centralized'
import CsgLazyRoute from 'components/molecules/LazyRoute'
import CsgButtonSupport from 'components/molecules/ButtonSupport'

import authService from 'services/auth'
import ConvenioService from 'services/convenio'
import config from 'app/config'

import { setContextParameters } from 'store/actionCreators/contextParameters'

// Google Analytics
import ReactGA from 'react-ga'
import { MatriculaModalPaginationProvider } from 'hooks/useMatriculaModalPagination'

const CsgComprovante = lazy(() => import('components/pages/Comprovante'))
const CsgAuthPage = lazy(() => import('components/pages/Auth'))
const CsgAdminPage = lazy(() => import('components/pages/Admin'))
const CsgNotFoundPage = lazy(() => import('components/pages/NotFound'))

const styles = theme => ({
  root: {
    position: 'relative'
  },
  loading: {
    top: 0,
    left: 0,
    width: '100%',
    height: 5,
    position: 'fixed',
    zIndex: theme.zIndex.loading
  },
  linearColorPrimary: {
    backgroundColor: theme.palette.warning.main
  },
  linearBarColorPrimary: {
    backgroundColor: theme.palette.warning.light
  }
})

const LoadingComponents = () => (
  <CsgCentralizedTemplate>
    <MuiCircularProgress />
  </CsgCentralizedTemplate>
)

const queryClient = new QueryClient()

const App = ({ me, classes, loading }) => {
  return (
    <>
      {R.isNil(me) ? (
        <LoadingComponents />
      ) : (
        <QueryClientProvider client={queryClient}>
          <VisibilityProvider>
            <MatriculaModalPaginationProvider>
              <div className={classes.root}>
                {loading && (
                  <MuiLinearProgress
                    className={classes.loading}
                    classes={{
                      colorPrimary: classes.linearColorPrimary,
                      barColorPrimary: classes.linearBarColorPrimary
                    }}
                  />
                )}

                <Switch>
                  <CsgLazyRoute path="/comprovante" component={CsgComprovante} />
                  <CsgLazyRoute path="/admin" component={CsgAdminPage} />
                  <CsgLazyRoute path="/auth" component={CsgAuthPage} />
                  <Route path="/" exact render={() => <Redirect to="/auth/login" />} />
                  <CsgLazyRoute component={CsgNotFoundPage} />
                </Switch>
              </div>
            </MatriculaModalPaginationProvider>
          </VisibilityProvider>
        </QueryClientProvider>
      )}
      <CsgButtonSupport linkWhatsApp={config.system.linkWhatsApp} linkFreshDesk={config.system.linkFreshDesk} />
    </>
  )
}

const mapStateToProps = ({ loading, me }) => ({
  me,
  ...loading
})

const enhance = compose(
  withStyles(styles),
  connect(mapStateToProps),
  withState('hasWarning', 'setHasWarning', false),
  lifecycle({
    async componentDidMount() {
      initializeReactGA()
      const idConvenio = R.path(['context', 'idConvenio'], await authService.me())
      ConvenioService.getParametros(idConvenio).then(
        R.compose(this.props.dispatch, setContextParameters, R.omit(['_links']), R.prop('data'))
      )
    }
  })
)

function initializeReactGA() {
  ReactGA.initialize('UA-156197772-1')
  ReactGA.pageview(window.location.pathname)
}

export default enhance(App)
